// mui
import {
  Box,
  Card,
  Stack,
  Typography,
  type Theme,
  type TypographyProps,
} from '@mui/material';

// components
import { Icon } from '@/components/atoms/Icon/Icon';
import type { Icons } from '@/components/atoms/Icon/icons';

export type ChatActionRowTone = 'offer' | 'go' | 'attached' | 'menu';

export interface ChatActionRowProps {
  label: string;
  icon?: Icons;
  /**
   * `go` marks the way on and takes the selected surface; `attached` is joined
   * to the question above; `menu` is an answer under a menu message, centred.
   */
  tone?: ChatActionRowTone;
  /** Attached and menu only: a quieter row for what is not the answer, like Do this later. */
  isQuiet?: boolean;
  /** Attached and menu only: the answer picked, marked out from the rest. */
  isChosen?: boolean;
  isDisabled?: boolean;
  /** Shown as it is, not to be tapped: an answer in the history. */
  isReadOnly?: boolean;
  onClick: () => void;
}

const CARD_VARIANT = {
  offer: 'chatOffer',
  go: 'chatGo',
  attached: 'chatAttached',
  menu: 'chatAttached',
} as const;

const QUIET_LABEL: TypographyProps = {
  variant: 'body1',
  sx: { color: 'custom.neutral.600' },
};

/** How each tone sets its label, as an answer and as a quieter row. */
const LABEL: Record<
  ChatActionRowTone,
  { answer: TypographyProps; quiet: TypographyProps }
> = {
  offer: { answer: { variant: 'body1' }, quiet: { variant: 'body1' } },
  go: { answer: { variant: 'body1' }, quiet: { variant: 'body1' } },
  attached: {
    answer: { variant: 'subtitle1', color: 'primary.main' },
    quiet: QUIET_LABEL,
  },
  menu: {
    answer: { variant: 'subtitle1', sx: { color: 'primary.main' } },
    quiet: QUIET_LABEL,
  },
};

/** How each tone lays out its icon and label. */
const LAYOUT: Record<
  ChatActionRowTone,
  { spacing: number; iconSize: number; justifyContent?: 'center' }
> = {
  offer: { spacing: 1.5, iconSize: 16 },
  go: { spacing: 1.5, iconSize: 16 },
  attached: { spacing: 1.5, iconSize: 16 },
  menu: { spacing: 1, iconSize: 14, justifyContent: 'center' },
};

/** How long a menu answer's border takes to change colour once picked. */
const PICK_MS = 240;

/** A menu's answers ease into their picked border, and ring where the keyboard is. */
const MENU_SX = {
  transition: `border-color ${PICK_MS}ms ease, transform 120ms ease`,
  '&:focus-visible': {
    outline: (theme: Theme) => `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '-2px',
  },
};

/** ChatActionRow is a tappable row in a chat thread. */
export const ChatActionRow = ({
  label,
  icon,
  tone = 'offer',
  isQuiet = false,
  isChosen = false,
  isDisabled = false,
  isReadOnly = false,
  onClick,
}: ChatActionRowProps) => (
  <Card
    {...(isReadOnly
      ? { component: 'div', role: 'note' }
      : { component: 'button', type: 'button', disabled: isDisabled, onClick })}
    aria-label={label}
    variant={CARD_VARIANT[tone]}
    sx={{
      ...(tone === 'menu' && MENU_SX),
      ...(isChosen && { borderColor: 'primary.main' }),
    }}
  >
    <Stack
      direction='row'
      spacing={LAYOUT[tone].spacing}
      sx={{
        alignItems: 'center',
        justifyContent: LAYOUT[tone].justifyContent,
      }}
    >
      {icon && (
        <Box sx={{ display: 'flex', color: 'primary.main' }}>
          <Icon icon={icon} fontSize={LAYOUT[tone].iconSize} />
        </Box>
      )}
      <Typography {...LABEL[tone][isQuiet ? 'quiet' : 'answer']}>
        {label}
      </Typography>
    </Stack>
  </Card>
);

ChatActionRow.displayName = 'ChatActionRow';
export default ChatActionRow;
